(() => {
  if (customElements.get('aeternum-bundle-builder')) return;
  class AeternumBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.items = new Map();
      this.rates = { 2: Number(this.dataset.tierTwo), 3: Number(this.dataset.tierThree), 4: Number(this.dataset.tierFour) };
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.cards = [...this.querySelectorAll('.abb__card')];
      this.codes = { 2: this.dataset.codeTwo, 3: this.dataset.codeThree, 4: this.dataset.codeFour };
      this.eligibleProducts = new Set(this.dataset.eligibleProducts.split(','));
      this.cartRoot = this.dataset.cartRoot.endsWith('/') ? this.dataset.cartRoot : this.dataset.cartRoot + '/';
      this.cards.forEach(card => {
        card.querySelectorAll('[data-variant] option').forEach(option => {
          if (this.regionalRoute(option)) {
            option.disabled = false;
            option.textContent += ' — Unavailable in your region';
          }
        });
      });
      this.restoreSelection();
      window.addEventListener('pageshow', () => { this.setBusy(false); this.render(); });
      this.addEventListener('click', event => {
        if (this.busy) return;
        const button = event.target.closest('button[data-action]');
        if (!button || !this.contains(button)) return;
        const action = button.dataset.action;
        if (action === 'view-summary') { this.querySelector('.abb__summary').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }); return; }
        if (action === 'checkout') { this.checkout(); return; }
        if (action === 'remove') {
          const item = this.items.get(button.dataset.variantId);
          this.items.delete(button.dataset.variantId);
          this.render(item ? item.title + ' removed.' : 'Item removed.');
          this.querySelector('[data-progress-heading]').setAttribute('tabindex', '-1');
          this.querySelector('[data-progress-heading]').focus({ preventScroll: true });
          return;
        }
        const card = button.closest('.abb__card');
        if (!card) return;
        const option = card.querySelector('[data-variant]').selectedOptions[0];
        if (!option) return;
        const issue = this.availabilityIssue(option);
        if (issue) { this.showAvailabilityNotice(option, issue); return; }
        const id = option.value;
        const existing = this.items.get(id);
        const quantity = Math.max(0, Math.min(99, (existing?.quantity || 0) + (action === 'decrease' ? -1 : 1)));
        const title = card.dataset.title;
        if (quantity) this.items.set(id, { id, title, variant: option.dataset.variantTitle, price: Number(option.dataset.price), quantity });
        else this.items.delete(id);
        this.render(title + ': ' + quantity + ' selected.');
        // Keep focus on a visible control when Add becomes a quantity selector, or quantity returns to zero.
        if (action === 'add') card.querySelector('[data-action="increase"]').focus({ preventScroll: true });
        else if (!quantity) card.querySelector('[data-action="add"]').focus({ preventScroll: true });
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-variant]')) this.render();
      });
      this.render();
      if (this.restoredUnavailable) this.showAvailabilityNotice(this.restoredUnavailable.option, this.restoredUnavailable.issue);
    }
    regionalRoute(option) {
      const link = option?.closest('.abb__card')?.querySelector('[data-details], .abb__details');
      if (!link) return null;
      const handle = new URL(link.href, window.location.href).pathname.match(/\/products\/([^/]+)/)?.[1];
      return window.AERegionalWaitlist?.findRoute?.(handle, option.value, 'cart') || null;
    }
    availabilityIssue(option) {
      if (typeof window.AERegionalWaitlist?.findRoute !== 'function') {
        return { title: 'Unable to confirm availability', message: 'Please refresh the page before adding products to your bundle.' };
      }
      const route = this.regionalRoute(option);
      if (route) return {
        title: 'Currently out of stock in your region',
        message: 'This option is unavailable in your selected region and cannot be added to your bundle. Please choose another available product or size.'
      };
      if (!option || option.dataset.available !== 'true') {
        return { title: 'Currently out of stock', message: 'This option cannot be added to your bundle. Please choose another available product or size.' };
      }
      return null;
    }
    showAvailabilityNotice(option, issue) {
      if (!this.availabilityDialog) {
        const dialog = document.createElement('dialog');
        dialog.className = 'abb__dialog';
        const heading = document.createElement('h2');
        heading.id = 'abb-availability-' + this.bundleId;
        const product = document.createElement('p');
        const message = document.createElement('p');
        message.id = heading.id + '-message';
        const close = document.createElement('button');
        close.type = 'button'; close.className = 'abb__checkout'; close.textContent = 'Continue browsing';
        close.addEventListener('click', () => dialog.close());
        dialog.setAttribute('aria-labelledby', heading.id);
        dialog.setAttribute('aria-describedby', message.id);
        dialog.append(heading, product, message, close);
        this.append(dialog);
        this.availabilityDialog = { dialog, heading, product, message, close };
      }
      const { dialog, heading, product, message, close } = this.availabilityDialog;
      heading.textContent = issue.title;
      product.textContent = [option?.closest('.abb__card')?.dataset.title, option?.dataset.variantTitle].filter(Boolean).join(' — ');
      message.textContent = issue.message;
      this.availabilityDialog.waitlistForm?.remove();
      const route = this.regionalRoute(option);
      const service = window.AERegionalWaitlist;
      if (route && typeof service?.subscribe === 'function') {
        const form = this.createWaitlistForm(option, route, service);
        dialog.insertBefore(form, close);
        this.availabilityDialog.waitlistForm = form;
      }
      if (!dialog.open) dialog.showModal();
      close.focus();
    }
    createWaitlistForm(option, route, service) {
      const form = document.createElement('form');
      form.className = 'abb__waitlist';
      const label = document.createElement('label');
      const region = service.regionName?.(route.country) || route.country;
      label.textContent = 'Get notified when it’s back in your region (' + region + ').';
      const email = document.createElement('input');
      email.id = 'abb-waitlist-email-' + this.bundleId;
      email.type = 'email'; email.name = 'email'; email.required = true;
      email.autocomplete = 'email'; email.inputMode = 'email';
      email.placeholder = 'Your email address';
      label.htmlFor = email.id;
      const submit = document.createElement('button');
      submit.type = 'submit'; submit.className = 'abb__checkout'; submit.textContent = 'Notify me';
      const status = document.createElement('p');
      status.className = 'abb__waitlist-status'; status.id = email.id + '-status';
      status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
      email.setAttribute('aria-describedby', status.id);
      form.append(label, email, submit, status);
      let pending = false;
      let complete = false;
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (pending || complete) return;
        email.value = email.value.trim();
        if (!form.reportValidity()) return;
        // Resolve again before submitting; never reuse a route after a region change.
        if (window.AERegionalWaitlist !== service || this.regionalRoute(option) !== route) {
          status.textContent = 'Availability has changed. Please close this popup and try again.';
          status.dataset.error = 'true';
          return;
        }
        pending = true;
        email.disabled = true; submit.disabled = true;
        form.setAttribute('aria-busy', 'true');
        submit.textContent = 'Submitting…';
        status.textContent = ''; status.dataset.error = 'false';
        try {
          await service.subscribe(route, email.value, 'bundle_modal');
          complete = true;
          status.textContent = 'Request received for this option in ' + region + '. Check your inbox for any confirmation email.';
          submit.textContent = 'Notification requested';
        } catch (error) {
          status.textContent = 'We couldn’t save your request. Please try again.';
          status.dataset.error = 'true';
          submit.textContent = 'Notify me';
        } finally {
          pending = false;
          form.setAttribute('aria-busy', 'false');
          email.disabled = complete; submit.disabled = complete;
        }
      });
      return form;
    }
    pruneUnavailable() {
      let first = null;
      const options = [...this.querySelectorAll('[data-variant] option')];
      for (const [id] of this.items) {
        const option = options.find(candidate => candidate.value === id);
        const issue = this.availabilityIssue(option);
        if (issue) {
          this.items.delete(id);
          first ||= { option, issue };
        }
      }
      return first;
    }
    format(cents) { return this.money.format(cents / 100); }
    totals() {
      const count = [...this.items.values()].reduce((n, item) => n + item.quantity, 0);
      const rate = count >= 4 ? this.rates[4] : count >= 3 ? this.rates[3] : count >= 2 ? this.rates[2] : 0;
      const lines = [...this.items.values()].map(item => {
        const subtotal = item.price * item.quantity;
        const savings = Math.round(subtotal * rate / 100);
        return { ...item, subtotal, savings, total: subtotal - savings };
      });
      const subtotal = lines.reduce((n, item) => n + item.subtotal, 0);
      const savings = lines.reduce((n, item) => n + item.savings, 0);
      return { count, rate, lines, subtotal, savings, total: subtotal - savings };
    }
    render(announcement) {
      const removed = this.pruneUnavailable();
      if (removed) announcement = 'Unavailable items were removed from your bundle for this region.';
      const totals = this.totals();
      this.cards.forEach(card => {
        const option = card.querySelector('[data-variant]').selectedOptions[0];
        if (!option) return;
        const quantity = this.items.get(option.value)?.quantity || 0;
        const available = option.dataset.available === 'true';
        const regional = Boolean(this.regionalRoute(option));
        const guardReady = typeof window.AERegionalWaitlist?.findRoute === 'function';
        card.querySelector('[data-price]').textContent = this.format(Number(option.dataset.price));
        const add = card.querySelector('[data-action="add"]');
        add.hidden = quantity > 0;
        // Regional OOS remains clickable to explain why adding is blocked.
        add.disabled = !available && !regional && guardReady;
        add.textContent = !guardReady ? 'Check availability' : regional ? 'Out of stock in your region' : available ? 'Add to Bundle' : 'Sold out';
        if (regional || !guardReady) add.setAttribute('aria-haspopup', 'dialog');
        else add.removeAttribute('aria-haspopup');
        card.querySelector('[data-quantity]').hidden = quantity === 0;
        card.querySelector('[data-count]').textContent = quantity;
        card.querySelector('[data-action="increase"]').disabled = quantity >= 99;
        card.dataset.selected = String(quantity > 0);
      });
      const next = totals.count < 2 ? 2 : totals.count < 3 ? 3 : 4;
      this.querySelector('[data-progress-heading]').textContent = totals.count >= 4 ? 'You unlocked ' + totals.rate + '% off!' : 'Add ' + (next - totals.count) + ' more ' + (next - totals.count === 1 ? 'item' : 'items') + ' to save ' + this.rates[next] + '%';
      this.querySelector('[data-item-count]').textContent = totals.count + (totals.count === 1 ? ' item' : ' items');
      this.querySelector('[data-progress]').value = Math.min(4, totals.count);
      this.querySelectorAll('[data-milestone]').forEach(el => { el.dataset.reached = String(totals.count >= Number(el.dataset.milestone)); });
      this.querySelector('[data-empty]').hidden = totals.count > 0;
      const list = this.querySelector('[data-lines]');
      list.replaceChildren();
      totals.lines.forEach(item => {
        const li = document.createElement('li'); li.className = 'abb__line';
        const info = document.createElement('div');
        const title = document.createElement('p'); title.className = 'abb__line-title';
        title.textContent = item.title + (item.variant ? ' — ' + item.variant : '');
        const prices = document.createElement('div'); prices.className = 'abb__line-prices';
        if (item.savings) {
          const original = document.createElement('s'); original.textContent = this.format(item.subtotal); prices.append(original);
        }
        const price = document.createElement('strong'); price.textContent = this.format(item.total); prices.append(price);
        if (item.savings) {
          const savings = document.createElement('span'); savings.className = 'abb__line-savings';
          savings.textContent = this.format(item.savings) + ' savings'; prices.append(savings);
        }
        info.append(title, prices);
        const controls = document.createElement('div'); controls.className = 'abb__line-controls';
        const remove = document.createElement('button'); remove.type = 'button'; remove.dataset.action = 'remove'; remove.dataset.variantId = item.id;
        remove.setAttribute('aria-label', 'Remove ' + title.textContent + ' from bundle'); remove.textContent = '×';
        const quantity = document.createElement('span'); quantity.textContent = '× ' + item.quantity;
        controls.append(remove, quantity); li.append(info, controls); list.append(li);
      });
      this.querySelector('[data-subtotal]').textContent = this.format(totals.subtotal);
      this.querySelector('[data-savings]').textContent = '−' + this.format(totals.savings);
      this.querySelector('[data-rate]').textContent = totals.rate ? '(' + totals.rate + '%)' : '';
      this.querySelector('[data-total]').textContent = this.format(totals.total);
      this.querySelector('[data-action="checkout"]').disabled = totals.count === 0;
      this.querySelector('[data-mobile-total]').textContent = totals.count + (totals.count === 1 ? ' item · ' : ' items · ') + this.format(totals.total);
      try { sessionStorage.setItem('aeternum-bundle-checkout-v1', JSON.stringify({ id: this.bundleId, items: [...this.items.values()] })); } catch (_) { /* Selection still works when storage is unavailable. */ }
      if (announcement) this.querySelector('[data-announcement]').textContent = announcement + ' ' + totals.count + (totals.count === 1 ? ' item. Bundle total ' : ' items. Bundle total ') + this.format(totals.total) + '.';
    }
    restoreSelection() {
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem('aeternum-bundle-checkout-v1')); } catch (_) { /* Start a fresh selection. */ }
      this.bundleId = typeof saved?.id === 'string' ? saved.id : crypto.randomUUID();
      if (!Array.isArray(saved?.items)) return;
      for (const item of saved.items) {
        const option = [...this.querySelectorAll('[data-variant] option')].find(el => el.value === item.id && el.dataset.available === 'true');
        if (option && this.availabilityIssue(option)) {
          this.restoredUnavailable ||= { option, issue: this.availabilityIssue(option) };
          continue;
        }
        if (!option || !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 99) continue;
        const card = option.closest('.abb__card');
        this.items.set(option.value, { id: option.value, title: card.dataset.title, variant: option.dataset.variantTitle, price: Number(option.dataset.price), quantity: item.quantity });
        card.querySelector('[data-variant]').value = option.value;
      }
    }
    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.disabledControls = [...this.querySelectorAll('button,select')].map(el => [el, el.disabled]);
        this.disabledControls.forEach(([el]) => { el.disabled = true; });
      } else {
        this.disabledControls?.forEach(([el, disabled]) => { el.disabled = disabled; });
        this.disabledControls = null;
      }
      this.querySelector('[data-action="checkout"]').textContent = busy ? 'Preparing your checkout…' : 'Continue to checkout';
    }
    async cartRequest(path, body) {
      const response = await fetch(this.cartRoot + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { 'Accept': 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(30000)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Your cart could not be updated. Please try again.');
      return data;
    }
    async checkout() {
      if (this.busy || !this.items.size) return;
      const blocked = this.pruneUnavailable();
      if (blocked) {
        this.render('Unavailable items were removed from your bundle for this region.');
        this.showAvailabilityNotice(blocked.option, blocked.issue);
        return;
      }
      this.setBusy(true);
      const error = this.querySelector('[data-checkout-error]');
      error.hidden = true;
      try {
        let cart = await this.cartRequest('cart.js');
        const ownLines = () => cart.items.filter(line => line.properties?._aeternum_bundle === this.bundleId);
        // Reconcile only this builder's lines. Existing unrelated cart items are preserved.
        const adjusted = new Set();
        for (const original of ownLines()) {
          const current = ownLines().find(line => String(line.variant_id) === String(original.variant_id) && !adjusted.has(line.key));
          if (!current) continue;
          const desired = this.items.get(String(current.variant_id))?.quantity || 0;
          if (current.quantity !== desired) cart = await this.cartRequest('cart/change.js', { id: current.key, quantity: desired });
          adjusted.add(current.key);
        }
        const present = new Set(ownLines().map(line => String(line.variant_id)));
        const additions = [...this.items.values()].filter(item => !present.has(item.id)).map(item => ({ id: Number(item.id), quantity: item.quantity, properties: { _aeternum_bundle: this.bundleId } }));
        if (additions.length) await this.cartRequest('cart/add.js', { items: additions });
        cart = await this.cartRequest('cart.js');
        // Stop if inventory adjustment or a partial add prevented the requested selection.
        const actual = new Map(ownLines().map(line => [String(line.variant_id), line.quantity]));
        if ([...this.items.values()].some(item => actual.get(item.id) !== item.quantity)) throw new Error('Some quantities are unavailable. Please review your selection or your cart and try again.');
        const count = cart.items.reduce((sum, line) => sum + (this.eligibleProducts.has(String(line.product_id)) && !line.selling_plan_allocation ? line.quantity : 0), 0);
        const tier = count >= 4 ? 4 : count >= 3 ? 3 : count >= 2 ? 2 : 0;
        const code = this.codes[tier];
        if (code) {
          // Shopify validates eligible products and minimum quantities on the server.
          cart = await this.cartRequest('cart/update.js', { discount: code });
          const allocations = cart.items.flatMap(line => line.line_level_discount_allocations || []).map(allocation => allocation.discount_application);
          const applied = (cart.discount_codes || []).some(discount => discount.code.toUpperCase() === code.toUpperCase() && discount.applicable !== false)
            || [...(cart.cart_level_discount_applications || []), ...allocations].some(discount => String(discount.title).toUpperCase() === code.toUpperCase());
          // Shopify can choose an equal or better existing automatic discount instead of the code.
          const eligibleLines = cart.items.filter(line => this.eligibleProducts.has(String(line.product_id)) && !line.selling_plan_allocation);
          const equivalentOrBetter = eligibleLines.length > 0 && eligibleLines.every(line => Number.isFinite(line.final_line_price) && line.final_line_price <= Math.ceil(line.original_line_price * (100 - this.rates[tier]) / 100));
          if (!applied && !equivalentOrBetter) throw new Error('Your items are in the cart, but Shopify could not confirm the bundle discount. Please retry before checking out.');
        }
        window.location.assign(this.cartRoot + 'checkout');
      } catch (problem) {
        error.textContent = problem.name === 'TimeoutError' || problem.name === 'TypeError' ? 'Connection interrupted. Please try again; your bundle will not be added twice.' : problem.message;
        error.hidden = false;
        this.setBusy(false);
        this.render();
      }
    }
  }
  customElements.define('aeternum-bundle-builder', AeternumBundleBuilder);
})();



